import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { TraitBoundary } from "../api/request/Lead/Interfaces/TraitTypes";

interface UseTraitStateProps {
  initialCapitals: Record<string, number>;
  traitBoundaries: TraitBoundary[];
  globalAccumulatedMax: number;
  onRecalculate?: (capitalsByTrait: Record<string, number>) => Promise<void>;
}

interface UseTraitStateReturn {
  capitalsByTrait: Record<string, number>;
  traitBoundaries: TraitBoundary[];
  isTraitActive: (trait: string) => boolean;
  toggleTrait: (trait: string) => void;
  setTraitCapital: (trait: string, capital: number) => void;
  derivedCoverage: string;
  loading: boolean;
  totalCapital: number;
}

const DEBOUNCE_MS = 500;

export const useTraitState = ({
  initialCapitals,
  traitBoundaries,
  globalAccumulatedMax,
  onRecalculate,
}: UseTraitStateProps): UseTraitStateReturn => {
  const [capitalsByTrait, setCapitalsByTrait] = useState<Record<string, number>>(initialCapitals);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCapitalsByTrait(initialCapitals);
  }, [initialCapitals]);

  const boundaryMap = useMemo(() => {
    const map: Record<string, TraitBoundary> = {};
    for (const b of traitBoundaries) {
      map[b.trait] = b;
    }
    return map;
  }, [traitBoundaries]);

  const isTraitActive = useCallback(
    (trait: string): boolean => {
      return trait in capitalsByTrait;
    },
    [capitalsByTrait]
  );

  const totalCapital = useMemo(() => {
    return Object.values(capitalsByTrait).reduce((sum, val) => sum + val, 0);
  }, [capitalsByTrait]);

  const derivedCoverage = useMemo(() => {
    const hasDisability = "DISABILITY" in capitalsByTrait;
    const hasSeriousIllness = "SERIOUS_ILLNESS" in capitalsByTrait;

    if (hasDisability && hasSeriousIllness) return "multi";
    if (hasSeriousIllness) return "eg";
    if (hasDisability) return "premium";
    return "basic";
  }, [capitalsByTrait]);

  const scheduleRecalculate = useCallback(
    (newCapitals: Record<string, number>) => {
      if (!onRecalculate) return;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          await onRecalculate(newCapitals);
        } finally {
          setLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [onRecalculate]
  );

  const toggleTrait = useCallback(
    (trait: string) => {
      if (trait === "DEATH") return;

      setCapitalsByTrait((prev) => {
        const next = { ...prev };

        if (trait in next) {
          delete next[trait];
        } else {
          const boundary = boundaryMap[trait];
          if (boundary && boundary.available) {
            let defaultCapital =
              trait === "DISABILITY" && next["DEATH"] !== undefined
                ? Math.max(boundary.min, Math.min(next["DEATH"], boundary.max))
                : boundary.min;

            const currentTotal = Object.values(next).reduce((sum, val) => sum + val, 0);
            if (globalAccumulatedMax > 0 && currentTotal + defaultCapital > globalAccumulatedMax) {
              defaultCapital = Math.max(boundary.min, globalAccumulatedMax - currentTotal);
              if (defaultCapital < boundary.min) {
                return prev;
              }
            }

            next[trait] = defaultCapital;
          }
        }

        scheduleRecalculate(next);
        return next;
      });
    },
    [boundaryMap, globalAccumulatedMax, scheduleRecalculate]
  );

  const setTraitCapital = useCallback(
    (trait: string, capital: number) => {
      setCapitalsByTrait((prev) => {
        if (!(trait in prev)) return prev;

        const boundary = boundaryMap[trait];
        let clampedCapital = capital;

        if (boundary) {
          clampedCapital = Math.max(boundary.min, Math.min(boundary.max, clampedCapital));
        }

        const otherTotal = Object.entries(prev)
          .filter(([key]) => key !== trait)
          .reduce((sum, [, val]) => sum + val, 0);

        if (globalAccumulatedMax > 0 && otherTotal + clampedCapital > globalAccumulatedMax) {
          clampedCapital = Math.max(
            boundary?.min ?? 0,
            globalAccumulatedMax - otherTotal
          );
        }

        const next = { ...prev, [trait]: clampedCapital };
        scheduleRecalculate(next);
        return next;
      });
    },
    [boundaryMap, globalAccumulatedMax, scheduleRecalculate]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    capitalsByTrait,
    traitBoundaries,
    isTraitActive,
    toggleTrait,
    setTraitCapital,
    derivedCoverage,
    loading,
    totalCapital,
  };
};

export default useTraitState;
